import type { HTMLAttributes, ReactNode } from 'react';
import { Menu as MenuIcon, SidebarLeft, SidebarRight } from 'iconsax-reactjs';
import { IconButton, cx } from '@/components/ui';
import { useAppShell } from '../AppShell/appShellContext';
import styles from './TopBar.module.css';

export interface TopBarProps extends HTMLAttributes<HTMLElement> {
  /** Context selectors, breadcrumbs... (after the navigation toggle). */
  left?: ReactNode;
  /** Usually the <GlobalSearch /> trigger. */
  center?: ReactNode;
  /** Notifications, theme toggle, user menu. */
  right?: ReactNode;
  /** Show the built-in navigation toggle (requires <AppShell>). Default true. */
  showNavToggle?: boolean;
}

export function TopBar({
  left,
  center,
  right,
  showNavToggle = true,
  className,
  ...rest
}: TopBarProps) {
  return (
    <header className={cx(styles.bar, className)} {...rest}>
      <div className={styles.left}>
        {showNavToggle && <NavToggleButton />}
        {left}
      </div>
      {center && <div className={styles.center}>{center}</div>}
      {right && <div className={styles.right}>{right}</div>}
    </header>
  );
}

/**
 * Opens the navigation drawer on small screens, or collapses/expands the icon rail on desktop.
 * Renders nothing outside <AppShell>.
 */
export function NavToggleButton({ className }: { className?: string }) {
  const shell = useAppShell();
  if (!shell) return null;

  if (!shell.isDesktop) {
    return (
      <IconButton
        className={className}
        label="Open navigation"
        icon={<MenuIcon size={20} color="currentColor" />}
        aria-expanded={shell.mobileNavOpen}
        aria-controls={shell.navId}
        onClick={shell.openMobileNav}
        tooltip={false}
      />
    );
  }

  return (
    <IconButton
      className={className}
      label={shell.collapsed ? 'Expand navigation' : 'Collapse navigation'}
      icon={
        shell.collapsed ? (
          <SidebarRight size={20} color="currentColor" />
        ) : (
          <SidebarLeft size={20} color="currentColor" />
        )
      }
      aria-expanded={!shell.collapsed}
      aria-controls={shell.navId}
      onClick={shell.toggleCollapsed}
    />
  );
}
